import { TextField, SelectField, CheckboxField, type CreateDataObjectFormData } from '../forms';
import { createDataObjectSchema } from '../forms/validation';
import { EntityForm } from '../shared';
import { classificationOptions, lifecycleStatusOptions } from '../../lib/selectOptions';
import '@tylertech/forge/card';
import './DataObjectForm.css';

export interface DataObjectFormProps {
  domainId: string;
  defaultValues?: Partial<CreateDataObjectFormData>;
  onSubmit: (data: CreateDataObjectFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
  mode: 'create' | 'edit';
}

const objectTypeOptions = [
  { value: 'entity', label: 'Entity' },
  { value: 'event', label: 'Event' },
  { value: 'aggregate', label: 'Aggregate' },
  { value: 'document', label: 'Document' },
  { value: 'report', label: 'Report' },
  { value: 'reference', label: 'Reference' },
];

const CardElement = 'forge-card' as React.ElementType;

export function DataObjectForm({
  defaultValues,
  onSubmit,
  onCancel,
  isSubmitting = false,
  mode,
}: DataObjectFormProps): JSX.Element {
  return (
    <EntityForm<CreateDataObjectFormData>
      schema={createDataObjectSchema}
      baseValues={{
        classification: 'internal',
        lifecycleStatus: 'active',
        containsPII: false,
        containsPCI: false,
        containsPHI: false,
        encryptionRequired: false,
        maskingRequired: false,
        isGoldenRecord: false,
      }}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      onCancel={onCancel}
      isSubmitting={isSubmitting}
      mode={mode}
      createLabel="Add Data Object"
      editLabel="Update Data Object"
      formClassName="data-object-form"
      actionsClassName="form-actions form-actions--compact"
    >
      {({ register, formState: { errors }, values: formValues }) => (
        <>
          <CardElement className="form-card form-card--compact">
            <div className="form-section form-section--compact">
              <h2 className="forge-typography--headline6">Basic Information</h2>

              <TextField
                label="Data Object Name"
                required
                error={errors.name}
                {...register('name')}
              />

              <TextField
                label="Description"
                error={errors.description}
                {...register('description')}
              />

              <SelectField
                label="Object Type"
                required
                options={objectTypeOptions}
                error={errors.objectType}
                /* c8 ignore next */
                value={formValues.objectType ?? ''}
                {...register('objectType')}
              />

              <SelectField
                label="Classification"
                required
                options={classificationOptions}
                error={errors.classification}
                /* c8 ignore next */
                value={formValues.classification ?? ''}
                {...register('classification')}
              />

              <SelectField
                label="Lifecycle Status"
                required
                options={lifecycleStatusOptions}
                error={errors.lifecycleStatus}
                /* c8 ignore next */
                value={formValues.lifecycleStatus ?? ''}
                {...register('lifecycleStatus')}
              />

              <TextField
                label="System of Record"
                error={errors.systemOfRecord}
                {...register('systemOfRecord')}
              />
            </div>
          </CardElement>

          <CardElement className="form-card form-card--compact">
            <div className="form-section form-section--compact">
              <h2 className="forge-typography--headline6">Privacy &amp; Security</h2>

              <div className="checkbox-group">
                <CheckboxField
                  label="Contains PII (Personally Identifiable Information)"
                  error={errors.containsPII}
                  {...register('containsPII')}
                />

                <CheckboxField
                  label="Contains PCI (Payment Card Industry Data)"
                  error={errors.containsPCI}
                  {...register('containsPCI')}
                />

                <CheckboxField
                  label="Contains PHI (Protected Health Information)"
                  error={errors.containsPHI}
                  {...register('containsPHI')}
                />

                <CheckboxField
                  label="Encryption Required at Rest"
                  error={errors.encryptionRequired}
                  {...register('encryptionRequired')}
                />

                <CheckboxField
                  label="Masking Required in Non-Production"
                  error={errors.maskingRequired}
                  {...register('maskingRequired')}
                />

                <CheckboxField
                  label="Is Golden Record (Authoritative Version)"
                  error={errors.isGoldenRecord}
                  {...register('isGoldenRecord')}
                />
              </div>

              <TextField label="Notes" error={errors.notes} {...register('notes')} />
            </div>
          </CardElement>
        </>
      )}
    </EntityForm>
  );
}
